import type { ProjectModel } from './projectmodel.js';

/**
 * Undo/redo-historie.
 *
 * Bewaart alleen projectmodellen. Media staan bewust NIET in het projectmodel
 * (alleen asset-id's), dus de geschiedenis blijft klein en er kan nooit per
 * ongeluk een mediabestand in de undo-stapel terechtkomen.
 *
 * De stapel is begrensd: bij een lange sessie mag het geheugengebruik niet
 * onbeperkt groeien.
 */
export interface HistorieStap {
  project: ProjectModel;
  /** Korte omschrijving, zodat de UI kan tonen wat ongedaan gemaakt wordt. */
  beschrijving: string;
  tijd: number;
}

export interface Historie {
  verleden: HistorieStap[];
  heden: HistorieStap;
  toekomst: HistorieStap[];
  maximum: number;
}

export function maakHistorie(project: ProjectModel, beschrijving = 'Begin'): Historie {
  return {
    verleden: [],
    heden: { project, beschrijving, tijd: Date.now() },
    toekomst: [],
    maximum: 100,
  };
}

export function kanOngedaanMaken(historie: Historie): boolean {
  return historie.verleden.length > 0;
}

export function kanOpnieuwDoen(historie: Historie): boolean {
  return historie.toekomst.length > 0;
}

/**
 * Voegt een nieuwe toestand toe. Alles wat "opnieuw doen" kon, vervalt: een
 * nieuwe wijziging maakt de oude toekomst ongeldig.
 */
export function duw(historie: Historie, project: ProjectModel, beschrijving: string): Historie {
  const verleden = [...historie.verleden, historie.heden];
  const teVeel = verleden.length - historie.maximum;
  return {
    verleden: teVeel > 0 ? verleden.slice(teVeel) : verleden,
    heden: { project, beschrijving, tijd: Date.now() },
    toekomst: [],
    maximum: historie.maximum,
  };
}

export function ongedaanMaken(historie: Historie): Historie {
  const vorige = historie.verleden[historie.verleden.length - 1];
  if (!vorige) return historie;
  return {
    ...historie,
    verleden: historie.verleden.slice(0, -1),
    heden: vorige,
    toekomst: [historie.heden, ...historie.toekomst],
  };
}

export function opnieuwDoen(historie: Historie): Historie {
  const volgende = historie.toekomst[0];
  if (!volgende) return historie;
  return {
    ...historie,
    verleden: [...historie.verleden, historie.heden],
    heden: volgende,
    toekomst: historie.toekomst.slice(1),
  };
}

/** Beschrijving van de stap die ongedaan gemaakt zou worden. */
export function volgendeOngedaanBeschrijving(historie: Historie): string | null {
  return historie.heden.beschrijving ?? null;
}
